/**
=========================================================
* Soft UI Dashboard React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import axios from 'axios';  
import React from 'react';  
import PlayerCollage from './PlayerCollage';

// @mui material components
import Card from "@mui/material/Card";
import { Box, Modal, IconButton, Typography, Chip } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import Parser from 'html-react-parser';

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import logo1 from "assets/images/logo_livecricketlogo.png";

//import Modal from 'react-bootstrap/Modal';

// Data
//import membersTableData from "layouts/groups/data/membersTableData";

import SoftAvatar from "components/SoftAvatar";

// Images
import team2 from "assets/images/team-2.jpg";

// Player Profile Modal Component
const PlayerProfileModal = ({ open, onClose, player }) => {
  if (!player) return null;

  // Helper function to check if a value is valid (not null, undefined, empty, or whitespace)
  const hasValue = (value) => {
    return value !== null && value !== undefined && String(value).trim() !== '';
  };

  // Helper function to check if is_icon is true (handles boolean, string "1", "true", etc.)
  const isIconPlayer = (isIcon) => {
    if (isIcon === null || isIcon === undefined) return false;
    if (typeof isIcon === 'boolean') return isIcon === true;
    if (typeof isIcon === 'string') {
      const trimmed = isIcon.trim().toLowerCase();
      return trimmed === '1' || trimmed === 'true' || trimmed === 'yes';
    }
    return Boolean(isIcon);
  };

  const formatSlNo = (slNo) => {
    if (!slNo) return '#000';
    const num = String(slNo).padStart(3, '0');
    return `#${num}`;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="player-profile-modal"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
          }
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          width: '100%',
          maxWidth: { xs: '90vw', sm: '520px', md: '560px' },
          maxHeight: '90vh',
          overflow: 'auto',
          outline: 'none',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Player ID Badge */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '4px 12px',
            borderRadius: '12px',
            fontWeight: 700,
            fontSize: '14px',
            color: '#333',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          {formatSlNo(player.sl_no)}
        </Box>

        {/* Icon Badge */}
        {isIconPlayer(player.is_icon) && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 56,
              zIndex: 10,
              backgroundColor: '#ffc107',
              color: '#000',
              padding: '4px 12px',
              borderRadius: '20px',
              fontWeight: 700,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: '16px' }} />
            ICON
          </Box>
        )}

        {/* Content */}
        <Box sx={{ p: 4, pt: 6 }}>
          {/* Profile Picture */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: { xs: '280px', sm: '360px' },
                height: { xs: '280px', sm: '360px' },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '300px', sm: '380px' },
                  height: { xs: '300px', sm: '380px' },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  opacity: 0.2,
                  filter: 'blur(20px)',
                }}
              />
              <img
                src={player.profile_pic || '/placeholder.svg'}
                alt={player.full_name}
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  border: '5px solid #e0e0e0',
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: '#f5f5f5',
                }}
              />
              {/* Icon Team Name Overlay on Profile Pic */}
              {hasValue(player.is_icon) && player.is_icon && hasValue(player.icon_text) && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#ffc107',
                    color: '#000',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    zIndex: 2,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  {player.icon_text}
                </Box>
              )}
            </Box>
          </Box>

          {/* Player Name */}
          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#222',
              mb: 1,
              fontSize: { xs: '24px', sm: '28px' },
            }}
          >
            {player.full_name || 'Unknown Player'}
          </Typography>

          {/* Role Badge */}
          {hasValue(player.role) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Chip
                label={player.role}
                sx={{
                  backgroundColor: '#4dd0e1',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '4px 16px',
                  height: '32px',
                  borderRadius: '20px',
                }}
              />
            </Box>
          )}

          {/* Batting and Bowling Styles */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            {/* Batting Style */}
            {hasValue(player.cric_batting_style) && (
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  BATTING
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: '#222',
                    fontSize: '14px',
                  }}
                >
                  {player.cric_batting_style}
                </Typography>
              </Box>
            )}

            {/* Bowling Style */}
            {hasValue(player.cric_bowling_style) && (
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  BOWLING
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: '#222',
                    fontSize: '14px',
                  }}
                >
                  {player.cric_bowling_style}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Additional Details */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* District Name */}
            {hasValue(player.district_name) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px',
                  backgroundColor: '#fafafa',
                  borderRadius: '12px',
                }}
              >
                <LocationOnIcon
                  sx={{
                    color: '#42a5f5',
                    fontSize: '24px',
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '12px',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    District
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#222',
                      fontSize: '15px',
                    }}
                  >
                    {player.district_name}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Previous Club */}
            {hasValue(player.previous_team_details) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px',
                  backgroundColor: '#fafafa',
                  borderRadius: '12px',
                }}
              >
                <GroupIcon
                  sx={{
                    color: '#42a5f5',
                    fontSize: '24px',
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '12px',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Previous Club
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#222',
                      fontSize: '15px',
                    }}
                  >
                    {player.previous_team_details}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Icon Team Name (if different from overlay) */}
            {isIconPlayer(player.is_icon) && hasValue(player.icon_text) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px',
                  backgroundColor: '#fff9c4',
                  borderRadius: '12px',
                  border: '1px solid #ffc107',
                }}
              >
                <EmojiEventsIcon
                  sx={{
                    color: '#f57c00',
                    fontSize: '24px',
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '12px',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Icon Team
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#222',
                      fontSize: '15px',
                    }}
                  >
                    {player.icon_text}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default class Miplain extends React.Component {  
  constructor(props) {
    super(props);
    this.setState({loggedIn: '1'});
    this.setState({showModal: false});
    this.setState({viewstatus: 'list'});

    this.state = {
        series_name: 'Champions League',
        team_name: 'Dream Team',
        players: [
          { full_name: 'Player 1', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 2', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 3', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 4', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 5', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 6', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 7', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 8', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 9', profile_pic: 'https://via.placeholder.com/100' },
          { full_name: 'Player 10', profile_pic: 'https://via.placeholder.com/100' },
          // Add more players as needed
        ],
        profileModalOpen: false,
        selectedPlayer: null
      }
  }
 

  componentDidMount() { 

    
    this.getbasicajax();

      

  }  

  getbasicajax()
  {
    let search = window.location.search;
    let params = new URLSearchParams(search);
    let invite_code = params.get('t');
    //alert(invite_code)
    const post_data = { team_hash_code: invite_code };
    
//      alert(group_id)
//,{headers: {"Content-Type": "application/json"}}
  axios.post( global.config.app.backend_api_url + `auction/get_team_info`,post_data, { withCredentials: true }
  )  
    .then(res => {  
      //const normalmembers = res.data;  
      //console.log("after112")
     // console.log(res.data)
      
      this.setState( {series_name: res.data.team[0].series_name
        ,team_name: res.data.team[0].team_name
        ,team_logo : res.data.team[0].team_logo
        ,league_logo : res.data.team[0].league_logo
        ,auction_link  : res.data.team[0].auction_link
        ,players  : res.data.team[0].players
        }
        );

       
    });
  }

  handleOpenPlayerProfile = (player) => {
    this.setState({
      selectedPlayer: player,
      profileModalOpen: true
    });
  }

  handleClosePlayerProfile = () => {
    this.setState({
      profileModalOpen: false,
      selectedPlayer: null
    });
  }
  
  
  
  
  

  render() {  

    return (  
      <SoftBox>
      
      <PlayerCollage
        seriesName={this.state.series_name}
        teamName={this.state.team_name}
        teamLogo={this.state.team_logo}
        leagueLogo = {this.state.league_logo}
        ourLogo = {logo1}
        players={this.state.players}
        auctionLink={this.state.auction_link}
        onPlayerClick={this.handleOpenPlayerProfile}
      />
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
  {this.state.league_logo ? (
    <img
      src={this.state.league_logo}
      alt="League Logo"
      style={{ maxHeight: '200px', marginRight: '20px' }}
    />
  ) : null}

  {/*<img src={logo1} width="200px" alt="Logo 1" />*/}
  </div>

      <PlayerProfileModal
        open={this.state.profileModalOpen}
        onClose={this.handleClosePlayerProfile}
        player={this.state.selectedPlayer}
      />

    </SoftBox>
    )  
  }  
}  
